import { alpha, Theme } from '@mui/material'

const useStyles = {
  spinner: {
    margin: 'auto',
  },
  modalContent: {
    padding: 0,
  },
  // The dialog wraps its content in an extra element, so it must also fill the fixed-height paper for the list to scroll
  desktopModalContent: {
    padding: 0,
    flex: 1,
    flexDirection: 'column',
    flexWrap: 'nowrap',
    // The dialog centers its content on the main axis, which is vertical now that the content is a column
    justifyContent: 'flex-start',
    minHeight: 0,
    overflow: 'hidden',
    '& > *': {
      flex: 1,
      minHeight: 0,
    },
  },
  desktopPaper: {
    height: '590px',
  },
  container: () => ({
    position: 'relative',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    height: '100%',
    minHeight: '200px',
  }),
  loadingOverlay: (theme: Theme) => ({
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(theme.palette.common.white, 0.6),
  }),
  breadcrumbs: (theme: Theme) => ({
    alignItems: 'center',
    flexWrap: 'nowrap',
    minWidth: 0,
    overflow: 'hidden',
    paddingBottom: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  }),
  breadcrumbItem: (isLast: boolean) => ({
    alignItems: 'center',
    flexWrap: 'nowrap',
    flex: isLast ? '0 1 auto' : '0 0 auto',
    minWidth: 0,
  }),
  breadcrumbSeparator: (theme: Theme) => ({
    flexShrink: 0,
    paddingX: theme.spacing(0.5),
    color: theme.palette.text.disabled,
  }),
  breadcrumbLink: {
    display: 'block',
    flex: '1 1 auto',
    width: 'auto',
    minWidth: 0,
    maxWidth: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  breadcrumbText: (theme: Theme) => ({
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.primary.dark,
    fontWeight: 500,
    letterSpacing: '0.25px',
  }),
  breadcrumbMeasureContainer: {
    position: 'absolute',
    visibility: 'hidden',
    width: 0,
    height: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  breadcrumbMeasure: {
    position: 'absolute',
    width: 'max-content',
    whiteSpace: 'nowrap',
  },
  homeButton: (theme: Theme) => ({
    flexShrink: 0,
    padding: theme.spacing(1),
    color: theme.palette.primary.dark,
  }),
  homeIcon: (theme: Theme) => ({
    fontSize: '16px',
    color: theme.palette.primary.dark,
  }),
  treeScroll: (theme: Theme) => ({
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    backgroundColor: theme.palette.ui.lightSurface,
    padding: theme.spacing(2),
  }),
  tree: (theme: Theme) => ({
    backgroundColor: theme.palette.common.white,
    borderRadius: '16px',
    padding: theme.spacing(1),
  }),
  childList: (theme: Theme) => ({
    marginLeft: theme.spacing(1),
  }),
  // Draws the vertical guide and the horizontal tick for each child; the last child's guide stops at its tick
  childRow: (theme: Theme, isLast: boolean) => ({
    position: 'relative',
    display: 'flex',
    paddingLeft: theme.spacing(3),
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: isLast ? '50%' : 0,
      borderLeft: `1px solid ${theme.palette.ui.outlineVariant}`,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      width: theme.spacing(1.5),
      borderTop: `1px solid ${theme.palette.ui.outlineVariant}`,
    },
  }),
  filterContainer: (theme: Theme) => ({
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.common.white,
  }),
  filterChip: (theme: Theme, isActive: boolean, isAllChip: boolean) => ({
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    minHeight: 32,
    paddingLeft: theme.spacing(1),
    paddingRight: isAllChip && isActive ? theme.spacing(2) : theme.spacing(1),
    border: `1px solid ${isActive ? 'transparent' : theme.palette.ui.outlineVariant}`,
    borderRadius: theme.spacing(1),
    backgroundColor: isActive ? theme.palette.secondary.light : theme.palette.common.white,
    color: theme.palette.text.secondary,
  }),
  filterCheckIcon: (theme: Theme) => ({
    fontSize: 18,
    color: theme.palette.text.secondary,
  }),
  filterDot: {
    marginLeft: 0,
  },
  filterLabel: {
    fontWeight: 500,
    letterSpacing: '0.25px',
  },
  dots: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  alertDot: (theme: Theme) => ({
    width: 10,
    height: 10,
    flexShrink: 0,
    marginLeft: theme.spacing(1),
    borderRadius: '50%',
    backgroundColor: theme.palette.error.main,
  }),
  incompleteDot: (theme: Theme) => ({
    width: 10,
    height: 10,
    flexShrink: 0,
    marginLeft: theme.spacing(1),
    borderRadius: '50%',
    background: `linear-gradient(90deg, ${theme.palette.ui.primary50} 50%, ${theme.palette.ui.surfaceDim} 50%)`,
  }),
  folderRowWrapper: {
    alignItems: 'center',
    flexWrap: 'nowrap',
  },
  folderRow: (theme: Theme) => ({
    flex: 1,
    justifyContent: 'flex-start',
    textAlign: 'left',
    padding: theme.spacing(1.5, 1),
    borderRadius: '8px',
  }),
  nameWrapper: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  selectedIcon: (theme: Theme) => ({
    color: theme.palette.primary.dark,
    marginLeft: theme.spacing(-1),
  }),
  folderName: (theme: Theme, isSelected: boolean) => ({
    color: theme.palette.primary.dark,
    fontWeight: isSelected ? 700 : 500,
  }),
  chevron: (theme: Theme) => ({
    color: theme.palette.primary.dark,
  }),
  searchToggle: (theme: Theme, isOpen: boolean) => ({
    backgroundColor: isOpen ? theme.palette.ui.surfaceContainerHighest : 'transparent',
  }),
  searchContainer: (theme: Theme) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
  }),
  searchInput: (theme: Theme) => ({
    height: 48,
    paddingX: theme.spacing(2),
    borderRadius: '28px',
    backgroundColor: theme.palette.ui.surfaceContainer,
    color: theme.palette.text.primary,
    '& input': {
      paddingLeft: theme.spacing(1.5),
    },
    '& input::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  }),
  clearIcon: (theme: Theme) => ({
    color: theme.palette.text.primary,
  }),
  searchIcon: (theme: Theme) => ({
    color: theme.palette.primary.dark,
  }),
  noResultsText: (theme: Theme) => ({
    padding: theme.spacing(2),
    fontWeight: 500,
    color: theme.palette.secondary.dark,
  }),
  emptyText: (theme: Theme) => ({
    padding: theme.spacing(2),
    textAlign: 'center',
  }),
}

export default useStyles
